import { useEffect, useState } from "react";
import { toast } from "sonner";
import { X } from "@phosphor-icons/react";
import { api, adminHeaders } from "@/lib/api";

const RESULT_LABEL = {
    ok: "tersimpan",
    ok_recording: "tersimpan (dari rekaman)",
    ok_event: "tersimpan (dari event)",
    offline: "kamera offline",
    no_footage: "tidak ada rekaman",
    already: "sudah ada",
    error: "gagal",
    no_camera: "tanpa kamera",
};
const KIND_LABEL = { schedule: "Jadwal", history: "Cari History", test: "Tes Jepret" };

const fmtWib = (iso) =>
    new Date(iso).toLocaleString("id-ID", {
        timeZone: "Asia/Jakarta",
        day: "numeric",
        month: "short",
        hour: "2-digit",
        minute: "2-digit",
    });

export const CctvSettings = ({ onClose }) => {
    const [s, setS] = useState(null);
    const [logs, setLogs] = useState([]);
    const [cameras, setCameras] = useState([]);
    const [camMsg, setCamMsg] = useState("");
    const [codeBusy, setCodeBusy] = useState("");
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        api.get("/admin/cctv/settings", { headers: adminHeaders() })
            .then((res) => setS(res.data))
            .catch(() => toast.error("Gagal memuat pengaturan CCTV"));
        api.get("/admin/cctv/logs", { headers: adminHeaders() })
            .then((res) => setLogs(res.data))
            .catch(() => {});
        api.get("/admin/cctv/cameras", { headers: adminHeaders() })
            .then((res) => setCameras(res.data.cameras))
            .catch(() => setCamMsg("Daftar kamera tidak tersedia"));
    }, []);

    const saveDeviceCode = async (serial, code) => {
        setCodeBusy(serial);
        try {
            await api.put("/admin/cctv/device-code", { device_serial: serial, device_code: code }, { headers: adminHeaders() });
            setCameras((prev) => prev.map((c) => (c.serial === serial ? { ...c, device_code: code } : c)));
            toast.success("Kode verifikasi disimpan");
        } catch {
            toast.error("Gagal menyimpan kode");
        } finally {
            setCodeBusy("");
        }
    };

    const save = async () => {
        setSaving(true);
        try {
            await api.put(
                "/admin/cctv/settings",
                {
                    master_enabled: s.master_enabled,
                    time_1: s.time_1,
                    time_2: s.time_2,
                    time_3: s.time_3,
                    time_4: s.time_4,
                    overtime_enabled: s.overtime_enabled,
                    overtime_time: s.overtime_time,
                },
                { headers: adminHeaders() }
            );
            toast.success("Pengaturan CCTV disimpan");
            onClose();
        } catch {
            toast.error("Gagal menyimpan pengaturan");
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose} data-testid="cctv-settings-overlay">
            <div className="bg-white border border-border w-full max-w-md max-h-[90vh] overflow-y-auto p-6 space-y-5" onClick={(e) => e.stopPropagation()}>
                <div className="flex items-start justify-between">
                    <div>
                        <p className="text-xs tracking-[0.2em] uppercase font-bold text-muted-foreground">Pengaturan</p>
                        <h2 className="font-serif text-2xl tracking-tight font-medium mt-1">CCTV EZVIZ</h2>
                    </div>
                    <button data-testid="cctv-settings-close" onClick={onClose} className="text-muted-foreground hover:text-foreground transition-colors" title="Tutup">
                        <X size={20} weight="light" />
                    </button>
                </div>

                {!s ? (
                    <p className="text-sm text-muted-foreground">Memuat...</p>
                ) : (
                    <>
                        <label className="flex items-center justify-between gap-4 border border-border p-4 cursor-pointer" data-testid="cctv-master-row">
                            <span>
                                <span className="block text-sm font-medium">Penjepretan Terjadwal</span>
                                <span className="block text-xs text-muted-foreground mt-0.5">
                                    {s.master_enabled ? "Aktif — sistem mengambil foto dari server EZVIZ sesuai jadwal" : "Nonaktif — tidak ada data yang diambil dari EZVIZ"}
                                </span>
                            </span>
                            <input
                                type="checkbox"
                                data-testid="cctv-master-toggle"
                                checked={s.master_enabled}
                                onChange={(e) => setS({ ...s, master_enabled: e.target.checked })}
                                className="w-5 h-5 accent-[#b5552d] shrink-0"
                            />
                        </label>

                        <div className="grid grid-cols-2 gap-3">
                            <label className="block">
                                <span className="block text-xs tracking-[0.15em] uppercase font-bold text-muted-foreground mb-1.5">Jadwal 1 (wajib)</span>
                                <input
                                    type="time"
                                    data-testid="cctv-time-1"
                                    value={s.time_1}
                                    onChange={(e) => setS({ ...s, time_1: e.target.value })}
                                    className="w-full border border-border px-3 py-2 text-sm outline-none focus:border-primary bg-transparent"
                                />
                            </label>
                            <label className="block">
                                <span className="block text-xs tracking-[0.15em] uppercase font-bold text-muted-foreground mb-1.5">Jadwal 2 (wajib)</span>
                                <input
                                    type="time"
                                    data-testid="cctv-time-2"
                                    value={s.time_2}
                                    onChange={(e) => setS({ ...s, time_2: e.target.value })}
                                    className="w-full border border-border px-3 py-2 text-sm outline-none focus:border-primary bg-transparent"
                                />
                            </label>
                            <label className="block">
                                <span className="block text-xs tracking-[0.15em] uppercase font-bold text-muted-foreground mb-1.5">Jadwal 3 (wajib)</span>
                                <input
                                    type="time"
                                    data-testid="cctv-time-3"
                                    value={s.time_3}
                                    onChange={(e) => setS({ ...s, time_3: e.target.value })}
                                    className="w-full border border-border px-3 py-2 text-sm outline-none focus:border-primary bg-transparent"
                                />
                            </label>
                            <label className="block">
                                <span className="block text-xs tracking-[0.15em] uppercase font-bold text-muted-foreground mb-1.5">Jadwal 4 (wajib)</span>
                                <input
                                    type="time"
                                    data-testid="cctv-time-4"
                                    value={s.time_4}
                                    onChange={(e) => setS({ ...s, time_4: e.target.value })}
                                    className="w-full border border-border px-3 py-2 text-sm outline-none focus:border-primary bg-transparent"
                                />
                            </label>
                        </div>

                        <div className="border border-border p-4">
                            <label className="flex items-center justify-between gap-4 cursor-pointer" data-testid="cctv-overtime-row">
                                <span>
                                    <span className="block text-sm font-medium">Lembur (jadwal tambahan)</span>
                                    <span className="block text-xs text-muted-foreground mt-0.5">Jepretan tambahan malam hari bila ada lembur</span>
                                </span>
                                <input
                                    type="checkbox"
                                    data-testid="cctv-overtime-toggle"
                                    checked={s.overtime_enabled}
                                    onChange={(e) => setS({ ...s, overtime_enabled: e.target.checked })}
                                    className="w-5 h-5 accent-[#b5552d] shrink-0"
                                />
                            </label>
                            {s.overtime_enabled && (
                                <input
                                    type="time"
                                    data-testid="cctv-overtime-time"
                                    value={s.overtime_time}
                                    onChange={(e) => setS({ ...s, overtime_time: e.target.value })}
                                    className="mt-3 w-full border border-border px-3 py-2 text-sm outline-none focus:border-primary bg-transparent"
                                />
                            )}
                        </div>

                        <div className="border-t border-border pt-4" data-testid="cctv-camera-codes">
                            <p className="text-xs tracking-[0.2em] uppercase font-bold text-muted-foreground mb-1">Kode Verifikasi Kamera</p>
                            <p className="text-xs text-muted-foreground mb-3">Satu kode per kamera (6 huruf di stiker kamera) — mengikuti kamera ke mana pun ia dipindah. Diperlukan untuk memutar rekaman.</p>
                            {camMsg && <p className="text-xs text-amber-700 mb-2">{camMsg}</p>}
                            {cameras.length === 0 && !camMsg ? (
                                <p className="text-xs text-muted-foreground">Memuat daftar kamera...</p>
                            ) : (
                                <div className="space-y-2">
                                    {cameras.map((c) => (
                                        <div key={c.serial} className="flex items-center gap-2" data-testid={`cctv-code-row-${c.serial}`}>
                                            <span className="text-sm flex-1 min-w-0 truncate">
                                                {c.name}
                                                {c.assigned_to && <span className="text-xs text-muted-foreground"> — {c.assigned_to.room_name}</span>}
                                            </span>
                                            <input
                                                data-testid={`cctv-code-input-${c.serial}`}
                                                defaultValue={c.device_code || ""}
                                                maxLength={6}
                                                placeholder="KODE"
                                                onChange={(e) => { e.target.value = e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, ""); }}
                                                className="w-24 border border-border px-2 py-1.5 text-sm text-center outline-none focus:border-primary bg-transparent font-mono tracking-[0.15em] uppercase"
                                            />
                                            <button
                                                data-testid={`cctv-code-save-${c.serial}`}
                                                onClick={(e) => {
                                                    const input = e.currentTarget.parentElement.querySelector("input");
                                                    saveDeviceCode(c.serial, input.value.trim());
                                                }}
                                                disabled={codeBusy === c.serial}
                                                className="border border-border px-3 py-1.5 text-xs hover:border-primary hover:text-primary transition-colors disabled:opacity-50"
                                            >
                                                Simpan
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>

                        {!s.configured && (
                            <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 px-3 py-2" data-testid="cctv-not-configured">
                                Kunci EZVIZ (AppKey & AppSecret) belum terpasang — penjepretan belum bisa berjalan.
                            </p>
                        )}

                        <button
                            data-testid="cctv-settings-save"
                            onClick={save}
                            disabled={saving}
                            className="w-full bg-primary text-primary-foreground py-3 text-sm font-medium hover:bg-[#9c4a2a] transition-colors disabled:opacity-50"
                        >
                            {saving ? "Menyimpan..." : "Simpan Pengaturan"}
                        </button>

                        <div className="border-t border-border pt-4" data-testid="cctv-log-section">
                            <p className="text-xs tracking-[0.2em] uppercase font-bold text-muted-foreground mb-2">Log Eksekusi</p>
                            {logs.length === 0 ? (
                                <p className="text-xs text-muted-foreground" data-testid="cctv-log-empty">Belum ada eksekusi tercatat.</p>
                            ) : (
                                <div className="max-h-56 overflow-y-auto divide-y divide-border" data-testid="cctv-log-list">
                                    {logs.map((l) => (
                                        <div key={l.id} className="py-2" data-testid={`cctv-log-${l.id}`}>
                                            <p className="text-xs font-mono">
                                                {fmtWib(l.at)} • {KIND_LABEL[l.kind] || l.kind} • {(l.duration_ms / 1000).toFixed(1)} dtk
                                            </p>
                                            <p className="text-xs text-muted-foreground mt-0.5">
                                                {l.skipped
                                                    ? `Dilewati — ${l.skipped === "master_off" ? "master toggle mati" : l.skipped}`
                                                    : l.entries.map((e) => `${e.room_name} ${e.label}: ${RESULT_LABEL[e.result] || e.result}`).join(" • ")}
                                            </p>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};
